import { extractPhoneAndExtension } from '@super/shared-types'
import {
  directoryApiClient,
  type ActionContact,
  type ContactActionType,
  type RoleWithMembers,
  type Staff,
} from '@/lib/api/directory.client'

export interface ParsedMentions {
  roleIds: string[]
  staffIds: string[]
  unknown: string[]
}

const MENTION_TERMINATOR = /[\s.,;:!?)\]\}]/

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Find every `@Name` in `text` whose Name matches a Role or Staff.
 *
 * Matching rules:
 *   - The `@` must be at the start of the string OR preceded by whitespace
 *     or an opening bracket (`(`, `[`, `{`). This rules out `ken@gmail.com`.
 *   - The Name must be followed by whitespace, end-of-string, or a single
 *     punctuation character (`. , ; : ! ? )` or a closing bracket).
 *   - Names are tested longest-first so `@Maintenance Lead` is matched
 *     before `@Maintenance` when both exist.
 *   - Comparison is case-insensitive.
 *
 * Returns deduped role + staff IDs and a list of `@Name` strings that
 * looked like mentions but didn't resolve to anything in the directory.
 */
export function parseMentions(
  text: string,
  roles: RoleWithMembers[],
  staff: Staff[],
): ParsedMentions {
  const candidates = [
    ...roles.map(role => ({
      kind: 'role' as const,
      id: role.id,
      name: role.name,
    })),
    ...staff.map(staffMember => ({
      kind: 'staff' as const,
      id: staffMember.id,
      name: staffMember.name,
    })),
  ].sort((left, right) => right.name.length - left.name.length)

  const roleIds = new Set<string>()
  const staffIds = new Set<string>()
  const unknown = new Set<string>()
  const matchedRanges: Array<[number, number]> = []

  const overlapsKnown = (start: number, end: number) =>
    matchedRanges.some(
      ([rangeStart, rangeEnd]) => start < rangeEnd && end > rangeStart,
    )

  for (const candidate of candidates) {
    const pattern = new RegExp(`@${escapeRegex(candidate.name)}`, 'gi')
    let match: RegExpExecArray | null
    while ((match = pattern.exec(text)) !== null) {
      const start = match.index
      const end = start + match[0].length
      const charBefore = start > 0 ? text[start - 1] : ''
      const charAfter = end < text.length ? text[end] : ''
      const validBefore = !charBefore || /[\s(\[\{]/.test(charBefore)
      const validAfter = !charAfter || MENTION_TERMINATOR.test(charAfter)
      if (!validBefore || !validAfter) continue
      if (overlapsKnown(start, end)) continue

      if (candidate.kind === 'role') roleIds.add(candidate.id)
      else staffIds.add(candidate.id)
      matchedRanges.push([start, end])
    }
  }

  // Collect anything that *looks* like a mention but didn't resolve. Match
  // a single name-like token (the warning is informational; a multi-word
  // name that's missing from the directory still surfaces under its first
  // word).
  const fallback = /(^|[\s(\[\{])@([A-Za-z][\w'-]*)/g
  let match: RegExpExecArray | null
  while ((match = fallback.exec(text)) !== null) {
    const offset = match.index + match[1].length
    const tag = `@${match[2]}`
    const end = offset + tag.length
    if (overlapsKnown(offset, end)) continue
    unknown.add(tag)
  }

  return {
    roleIds: [...roleIds],
    staffIds: [...staffIds],
    unknown: [...unknown],
  }
}

export type TargetChannel = 'phone' | 'email'

export type ParsedTarget =
  | { raw: string; kind: 'role'; roleId: string }
  | { raw: string; kind: 'staff'; staffId: string }
  | { raw: string; kind: 'literal-phone'; value: string }
  | { raw: string; kind: 'literal-email'; value: string }
  | { raw: string; kind: 'unknown'; reason: string }

// Accept common phone formats — leading +, digit, or `(` — plus an optional
// `#<digits>` extension at the end (e.g. (555) 555-5555, 555-555-5555#3).
const PHONE_RE = /^[+\d(][\d\s().-]{6,}(?:#\d{1,6})?$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Split a comma-separated target string from an action row into discrete
 * pieces and classify each one. Used for the inline fan-out preview and
 * the save-time `ActionContact` writes.
 *
 *   parseTargets("@Super, +15551234567", "phone", roles, staff)
 *     → [
 *         { raw: "@Super",        kind: "role",          roleId:  "..." },
 *         { raw: "+15551234567",  kind: "literal-phone", value:   "+15551234567" },
 *       ]
 *
 * Pieces that don't match the row's channel surface as `unknown` so the UI
 * can warn (e.g. "ken@x.com" inside a Call action).
 */
export function parseTargets(
  text: string,
  channel: TargetChannel,
  roles: RoleWithMembers[],
  staff: Staff[],
): ParsedTarget[] {
  const pieces = text
    .split(',')
    .map(piece => piece.trim())
    .filter(Boolean)

  const results: ParsedTarget[] = []

  for (const piece of pieces) {
    if (piece.startsWith('@')) {
      const parsed = parseMentions(piece, roles, staff)
      if (parsed.roleIds.length > 0) {
        results.push({ raw: piece, kind: 'role', roleId: parsed.roleIds[0] })
        continue
      }
      if (parsed.staffIds.length > 0) {
        results.push({ raw: piece, kind: 'staff', staffId: parsed.staffIds[0] })
        continue
      }
      results.push({
        raw: piece,
        kind: 'unknown',
        reason: 'not in directory',
      })
      continue
    }

    if (PHONE_RE.test(piece)) {
      if (channel === 'phone') {
        // Normalize to E.164 (with extension preserved) so dedupe across
        // different formats (e.g. 708-203-5932 vs +17082035932) works.
        const { phoneNumber, extension } = extractPhoneAndExtension(piece)
        const value = extension ? `${phoneNumber}#${extension}` : phoneNumber
        results.push({ raw: piece, kind: 'literal-phone', value })
      } else {
        results.push({
          raw: piece,
          kind: 'unknown',
          reason: 'phone number used in an email action',
        })
      }
      continue
    }

    if (EMAIL_RE.test(piece)) {
      if (channel === 'email') {
        results.push({ raw: piece, kind: 'literal-email', value: piece })
      } else {
        results.push({
          raw: piece,
          kind: 'unknown',
          reason: 'email used in a call/SMS action',
        })
      }
      continue
    }

    results.push({
      raw: piece,
      kind: 'unknown',
      reason: 'unrecognised format',
    })
  }

  return results
}

export interface ChipMentions {
  channel: ContactActionType
  roleIds: string[]
  staffIds: string[]
}

export interface SyncContactsArgs {
  actionGroupId: string
  mentions: ChipMentions[]
  existing: ActionContact[]
}

// Diff existing ActionContact rows against the chips and link/unlink to match.
// Legacy null-actionType rows get migrated to per-channel rows on first re-save.
export async function syncContactsForActionGroups({
  actionGroupId,
  mentions,
  existing,
}: SyncContactsArgs): Promise<{ created: number; deleted: number }> {
  type Mention = {
    target: string
    kind: 'role' | 'staff'
    channel: ContactActionType
  }

  const keyOf = (
    kind: 'role' | 'staff',
    target: string,
    channel: ContactActionType | null,
  ) => `${kind}:${target}:${channel ?? 'null'}`

  const desired: Mention[] = mentions.flatMap(chip => [
    ...chip.roleIds.map(target => ({
      target,
      kind: 'role' as const,
      channel: chip.channel,
    })),
    ...chip.staffIds.map(target => ({
      target,
      kind: 'staff' as const,
      channel: chip.channel,
    })),
  ])
  const desiredKeys = new Set(
    desired.map(mention =>
      keyOf(mention.kind, mention.target, mention.channel),
    ),
  )

  const existingByKey = new Map(
    existing.map(contact => {
      const kind = contact.roleId ? 'role' : ('staff' as const)
      const target = (contact.roleId ?? contact.staffId)!
      return [keyOf(kind, target, contact.actionType), contact] as const
    }),
  )

  const toLink = desired.filter(
    mention =>
      !existingByKey.has(keyOf(mention.kind, mention.target, mention.channel)),
  )
  const toUnlink = [...existingByKey.entries()]
    .filter(([key]) => !desiredKeys.has(key))
    .map(([, contact]) => contact)

  await Promise.all([
    ...toLink.map(mention =>
      mention.kind === 'role'
        ? directoryApiClient.linkRole(
            actionGroupId,
            mention.target,
            mention.channel,
          )
        : directoryApiClient.linkStaff(
            actionGroupId,
            mention.target,
            mention.channel,
          ),
    ),
    ...toUnlink.map(contact =>
      directoryApiClient.unlinkActionContact(contact.id),
    ),
  ])

  return { created: toLink.length, deleted: toUnlink.length }
}
